import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { authFetch } from '../lib/authFetch';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Printer, AlertCircle, Loader2 } from 'lucide-react';

/**
 * Driver Trip Sheet — authenticated, print-optimized page.
 *
 * The URL contains only the opaque booking UUID; every render requires a
 * valid session. Nothing on this page is publicly accessible.
 *
 * The server enforces status = 'Confirmed' unless an admin supplies an
 * override reason. This page:
 *   1. Prompts the admin for an override reason when the booking is not
 *      Confirmed (non-admins are rejected by the server with 403).
 *   2. Calls /trip-sheet-log to record who printed it and when.
 *   3. Renders a phone-printable layout that excludes internal notes, the
 *      audit trail, medical/diagnosis info, and billing.
 */
const DispatchBookingTripSheet = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [needsOverride, setNeedsOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [logging, setLogging] = useState(false);
  const [loggedAt, setLoggedAt] = useState(null);
  const [logError, setLogError] = useState('');

  const dispatchPhone = '832-925-2128';

  const fetchBooking = useCallback(async () => {
    setLoading(true);
    setError('');
    setNotFound(false);
    try {
      const res = await authFetch(`/api/bookings/${id}`);
      if (res.status === 404) { setNotFound(true); return; }
      if (!res.ok) { setError(`Unable to load request (HTTP ${res.status}).`); return; }
      const data = await res.json();
      setBooking(data);
    } catch (e) {
      setError('Network error while loading the request.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchBooking();
  }, [id, navigate, fetchBooking]);

  // Log the print event (server-side audit) as soon as booking is loaded and
  // status is Confirmed, or after admin submits an override reason.
  const logPrint = useCallback(async (reason) => {
    setLogging(true);
    setLogError('');
    try {
      const res = await authFetch(`/api/bookings/${id}/trip-sheet-log`, {
        method: 'POST',
        body: JSON.stringify(reason ? { override_reason: reason } : {}),
      });
      if (res.status === 403) {
        const body = await res.json().catch(() => ({}));
        setNeedsOverride(true);
        setLogError(body?.detail?.message || 'This request is not Confirmed. An admin override reason is required.');
        return false;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setLogError(typeof body.detail === 'string' ? body.detail : 'Unable to record print event.');
        return false;
      }
      const data = await res.json();
      setLoggedAt(data.logged_at_display);
      setNeedsOverride(false);
      return true;
    } catch (e) {
      setLogError('Network error while recording print event.');
      return false;
    } finally {
      setLogging(false);
    }
  }, [id]);

  useEffect(() => {
    if (!booking || loggedAt || needsOverride) return;
    if (booking.status === 'Confirmed') {
      logPrint('');
    } else {
      // Non-confirmed → require admin override reason before rendering full sheet
      setNeedsOverride(true);
    }
  }, [booking, loggedAt, needsOverride, logPrint]);

  const handleOverrideSubmit = async () => {
    if (!overrideReason.trim()) return;
    const ok = await logPrint(overrideReason.trim());
    if (ok) toast.success('Trip sheet unlocked. Print event recorded.');
  };

  const handlePrint = () => window.print();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="trip-sheet-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#004C97]" />
      </div>
    );
  }

  if (notFound || (!booking && error)) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
        <AlertCircle className="w-10 h-10 text-red-500" />
        <p className="mt-3 text-gray-800 font-semibold" data-testid="trip-sheet-error">
          {notFound ? 'Request not found' : 'Unable to load'}
        </p>
        <Button onClick={() => navigate('/dispatch/bookings')} className="mt-4">
          Back to Transportation Requests
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 print:bg-white">
      {/* Screen-only top bar (hidden in print) */}
      <div className="bg-[#004C97] text-white px-4 py-3 flex items-center gap-3 print:hidden">
        <Button
          onClick={() => navigate(`/dispatch/bookings/${id}`)}
          variant="ghost"
          className="text-white hover:bg-white/10 min-h-[44px]"
          data-testid="trip-sheet-back-btn"
        >
          <ArrowLeft className="w-5 h-5 mr-1" /> Back
        </Button>
        <h1 className="text-lg font-semibold flex-1">Driver Trip Sheet</h1>
        <Button
          onClick={handlePrint}
          disabled={needsOverride || !loggedAt}
          className="min-h-[44px] bg-[#F9A825] hover:bg-[#E08E1E] text-white disabled:opacity-50"
          data-testid="trip-sheet-print-btn"
        >
          <Printer className="w-4 h-4 mr-2" /> Print
        </Button>
      </div>

      {/* Admin override prompt (screen-only) */}
      {needsOverride && (
        <div className="max-w-2xl mx-auto p-4 print:hidden">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="trip-sheet-override-panel">
            <p className="text-sm font-semibold text-amber-900">
              This request is <span className="uppercase">{booking?.status}</span>, not Confirmed.
            </p>
            <p className="text-xs text-amber-800 mt-1">
              A driver trip sheet may only be generated by an authorized administrator
              with a recorded reason.
            </p>
            <textarea
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              rows={3}
              data-testid="trip-sheet-override-reason"
              placeholder="Reason for override (required)"
              className="mt-3 w-full min-h-[80px] px-3 py-2 text-base border border-amber-300 rounded-lg focus:ring-2 focus:ring-[#F9A825]"
            />
            {logError && (
              <p className="text-sm text-red-700 mt-2" data-testid="trip-sheet-override-error">{logError}</p>
            )}
            <div className="flex gap-2 mt-3">
              <Button
                onClick={() => navigate(`/dispatch/bookings/${id}`)}
                variant="outline"
                className="min-h-[44px]"
                data-testid="trip-sheet-override-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleOverrideSubmit}
                disabled={!overrideReason.trim() || logging}
                className="min-h-[44px] flex-1 bg-[#004C97] hover:bg-[#003a78] text-white"
                data-testid="trip-sheet-override-submit-btn"
              >
                {logging ? 'Recording…' : 'Continue with Override'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Trip sheet body */}
      {booking && loggedAt && !needsOverride && (
        <main
          className="max-w-2xl mx-auto p-4 sm:p-6 print:max-w-full print:p-4"
          data-testid="trip-sheet-body"
        >
          <div className="bg-white border border-gray-300 rounded-lg p-4 sm:p-6 print:border-0 print:rounded-none">
            {/* Header */}
            <div className="flex items-start justify-between border-b-2 border-[#004C97] pb-3 mb-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">MoveraCare Mobility</p>
                <h2 className="text-xl font-bold text-[#004C97]">Driver Trip Sheet</h2>
                <p className="text-xs text-gray-500 mt-1">
                  Dispatch: <span className="font-mono">{dispatchPhone}</span>
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500 uppercase">Reference</p>
                <p className="text-xl font-bold" data-testid="trip-sheet-ref">
                  {booking.ref || booking.id.slice(0, 8).toUpperCase()}
                </p>
                <p className="text-[10px] text-gray-500 mt-1">Printed {loggedAt}</p>
              </div>
            </div>

            {/* Trip essentials */}
            <table className="w-full text-sm border-collapse mb-4">
              <tbody>
                <TripRow label="Transportation Date" value={booking.date} testid="trip-sheet-date" />
                <TripRow label="Pickup Time" value={booking.time} testid="trip-sheet-time" />
                <TripRow label="Customer" value={booking.patient_name} testid="trip-sheet-customer" />
                <TripRow label="Customer Phone" value={booking.contact_phone} testid="trip-sheet-phone" />
                <TripRow
                  label="Pickup"
                  value={booking.pickup_address + (booking.pickup_unit ? `, Unit ${booking.pickup_unit}` : '')}
                  testid="trip-sheet-pickup"
                />
                <TripRow
                  label="Destination"
                  value={booking.dropoff_address + (booking.destination_unit ? `, Unit ${booking.destination_unit}` : '')}
                  testid="trip-sheet-destination"
                />
                <TripRow label="Transportation Mode" value={booking.mobility_type} testid="trip-sheet-mode" />
                <TripRow
                  label="Trip Type"
                  value={friendlyTripType(booking.trip_type) + (
                    booking.trip_type === 'round_trip'
                      ? ` (return ${booking.return_time_unknown ? 'TBD' : (booking.return_time || '—')})`
                      : ''
                  )}
                  testid="trip-sheet-trip-type"
                />
                {booking.facility_name && (
                  <TripRow label="Facility" value={booking.facility_name} testid="trip-sheet-facility" />
                )}
                <TripRow
                  label="Assigned Driver / Vehicle"
                  value={
                    (booking.assigned_driver_name || 'Unassigned')
                    + (booking.assigned_vehicle ? ` — ${booking.assigned_vehicle}` : '')
                  }
                  testid="trip-sheet-driver"
                />
              </tbody>
            </table>

            {/* Driver completion — pen-and-paper section for the trip */}
            <section className="border-t-2 border-dashed border-gray-300 pt-4">
              <h3 className="text-sm font-semibold text-gray-700 uppercase mb-3">Driver Completion</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <FillLine label="Pickup arrival time" />
                <FillLine label="Customer picked up at" />
                <FillLine label="Arrival at destination" />
                <FillLine label="Drop-off completed at" />
              </div>
              <FillBlock label="Driver notes" rows={3} />
              <FillBlock label="Signature (Driver)" rows={1} />
            </section>

            {/* Confidential footer */}
            <p
              className="text-[11px] text-gray-500 uppercase tracking-wider text-center mt-4 pt-3 border-t border-gray-200"
              data-testid="trip-sheet-confidential"
            >
              Confidential — Authorized Transportation Use Only
            </p>
          </div>
        </main>
      )}
    </div>
  );
};

function TripRow({ label, value, testid }) {
  return (
    <tr className="border-b border-gray-200" data-testid={testid}>
      <td className="py-2 pr-3 align-top text-xs text-gray-500 uppercase tracking-wide w-1/3">{label}</td>
      <td className="py-2 text-sm text-gray-900 break-words">{value || '—'}</td>
    </tr>
  );
}

function FillLine({ label }) {
  return (
    <div>
      <p className="text-[11px] text-gray-500 uppercase tracking-wide">{label}</p>
      <div className="h-8 border-b border-gray-400 mt-1" />
    </div>
  );
}

function FillBlock({ label, rows = 3 }) {
  return (
    <div className="mt-3">
      <p className="text-[11px] text-gray-500 uppercase tracking-wide">{label}</p>
      <div
        className="mt-1 border border-gray-300 rounded-md"
        style={{ height: `${rows * 26}px` }}
      />
    </div>
  );
}

function friendlyTripType(tt) {
  if (!tt) return '—';
  return { one_way: 'One-way', round_trip: 'Round-trip', wait_and_return: 'Wait & Return' }[tt] || tt;
}

export default DispatchBookingTripSheet;
